import { useMemo } from 'react';
import { TravelCurrencyCode } from '@graphql/generated-base';
import { useGeoBasedLocale, CURRENCY_PARAMS } from './useGeoLocale';
import { getLocalStorage } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { isTravelCurrencyCode, mapCurrencyCodeToTravelCurrencyCode } from '@shared/utils/currency';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { TRAVEL_CURRENCY_STORAGE_KEY } from '@shared/components/TravelCurrencySelector/TravelCurrencySelector';
import { useTravelCurrencyAllowlist } from './useTravelCurrencyAllowlist';

type UseTravelWhiteLabelCurrencyReturn = Readonly<{
  currency: TravelCurrencyCode;
  loading: boolean;
}>;

export const useTravelWhiteLabelCurrency = (
  eventCurrency?: TravelCurrencyCode | null,
  restrictToCurrencyCodes?: ReadonlyArray<TravelCurrencyCode>,
): UseTravelWhiteLabelCurrencyReturn => {
  const { currency: localeCurrency } = useGeoBasedLocale();
  const [isAccommodationsPageCurrencyEnabled] = useIsFeatureEnabled('accommodationsPageCurrencyEnabled');
  const { getValueString } = useQueryParamHelper();
  const { allowedSet, loading: isAllowlistLoading } = useTravelCurrencyAllowlist();

  const restrictionSet = useMemo(() => (restrictToCurrencyCodes ? new Set<string>(restrictToCurrencyCodes) : undefined), [restrictToCurrencyCodes]);
  const isAllowed = (code: string): boolean => allowedSet.has(code) && (!restrictionSet || restrictionSet.has(code));

  if (isAccommodationsPageCurrencyEnabled && eventCurrency) {
    const validatedEventCurrency = isAllowed(eventCurrency) ? eventCurrency : TravelCurrencyCode.USD;
    return { currency: validatedEventCurrency, loading: isAllowlistLoading };
  }

  // URL param takes highest priority — allows shared links to override stored preference.
  // Must also exist in the flag-controlled allowlist to prevent unsupported currencies from reaching APIs.
  const urlCurrency = getValueString(CURRENCY_PARAMS);
  if (urlCurrency && isTravelCurrencyCode(urlCurrency) && isAllowed(urlCurrency)) {
    return { currency: urlCurrency, loading: isAllowlistLoading };
  }

  const travelCurrency = getLocalStorage().getItem(TRAVEL_CURRENCY_STORAGE_KEY);
  if (travelCurrency && isTravelCurrencyCode(travelCurrency) && isAllowed(travelCurrency)) {
    return { currency: travelCurrency, loading: isAllowlistLoading };
  }

  // localeCurrency may itself originate from the URL param (via useGeoBasedLocale),
  // so it must also be validated against the allowlist to prevent bypass.
  const mappedCurrency = mapCurrencyCodeToTravelCurrencyCode(localeCurrency);
  if (isAllowed(mappedCurrency)) {
    return { currency: mappedCurrency, loading: isAllowlistLoading };
  }

  return { currency: TravelCurrencyCode.USD, loading: isAllowlistLoading };
};
